import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { ClientOnly, createFileRoute, notFound } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  BriefcaseIcon,
  Building2Icon,
  CalendarIcon,
  EditIcon,
  InfoIcon,
  Plus,
  TagIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react"
import { useId, useState } from "react"

import type { ExtendedProjectsResource } from "@repo/supabase/database-types"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { If } from "@repo/ui/components/ui/if"
import { toast } from "@repo/ui/components/ui/sonner"
import { toDisplayCurrency, toShortDate } from "~/lib/utils/localization"
import { AssignResourcesModal } from "~/modules/projects/components/assign-resources-modal"
import { EditResourceRatesModal } from "~/modules/projects/components/edit-resource-rates-modal"
import { RemoveResourceFromProjectModal } from "~/modules/projects/components/remove-resource-from-project-modal"
import { deleteProjectResourceFn } from "~/modules/projects/projects.functions"
import {
  getExtendedProjectQueryOptions,
  getProjectClientRatesQueryOptions,
} from "~/modules/projects/projects.queries"
import { useLanguageOptions } from "~/modules/system-config/hooks/use-language-options"
import { useSystemInvoiceItems } from "~/modules/system-config/hooks/use-system-invoice-items"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/projects/$projectId/overview",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const project = await context.queryClient.ensureQueryData(
      getExtendedProjectQueryOptions({ projectId: params.projectId }),
    )

    if (!project) {
      throw notFound()
    }
  },
})

function RouteComponent() {
  const params = Route.useParams()

  const { data: project } = useSuspenseQuery(
    getExtendedProjectQueryOptions({ projectId: params.projectId }),
  )

  const { getInvoiceItem } = useSystemInvoiceItems()
  const { data: projectClientRates } = useQuery({
    ...getProjectClientRatesQueryOptions({
      projectId: params.projectId ?? "",
      status: ["active"],
    }),
    enabled: !!params.projectId,
  })

  const { toDisplayLanguage } = useLanguageOptions()

  const toastId = useId()

  const [editRatesModalOpen, setEditRatesModalOpen] = useState(false)
  const [assignResourcesModalOpen, setAssignResourcesModalOpen] =
    useState(false)
  const [removeResourceModalOpen, setRemoveResourceModalOpen] = useState(false)
  const [selectedProjectResource, setSelectedProjectResource] =
    useState<ExtendedProjectsResource | null>(null)

  const deleteProjectResource = useServerFn(deleteProjectResourceFn)

  const deleteProjectResourceMutation = useMutation({
    mutationFn: deleteProjectResource,
    meta: {
      invalidates: [
        getExtendedProjectQueryOptions({ projectId: params.projectId })
          .queryKey,
      ],
    },
    onMutate: () => {
      toast.loading("Removing resource from project...", { id: toastId })
    },
    onSuccess: (result) => {
      if (!result.success) {
        toast.error(result.message, { id: toastId })
        return
      }

      toast.success("Resource removed from project", { id: toastId })
      setRemoveResourceModalOpen(false)
      setSelectedProjectResource(null)
    },
    onError: (error) => {
      toast.error("Error removing resource", {
        id: toastId,
        description: error.message,
      })
    },
  })

  const handleEditRates = (projectResource: ExtendedProjectsResource) => {
    setSelectedProjectResource(projectResource)
    setEditRatesModalOpen(true)
  }

  const handleRemoveResource = (projectResource: ExtendedProjectsResource) => {
    setSelectedProjectResource(projectResource)
    setRemoveResourceModalOpen(true)
  }

  const handleConfirmRemove = () => {
    if (selectedProjectResource) {
      deleteProjectResourceMutation.mutate({
        data: { projectResourceId: selectedProjectResource.id },
      })
    }
  }

  const handleAssignResources = () => {
    setAssignResourcesModalOpen(true)
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* Project Details */}
      <div>
        <div className="mb-4 flex items-center justify-between border-white/10 border-b pb-2">
          <h3 className="flex items-center gap-2 font-medium text-lg">
            <BriefcaseIcon className="h-5 w-5 text-white/70" /> Project Details
          </h3>
        </div>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <InfoIcon className="mt-0.5 h-5 w-5 text-white/70" />
            <div>
              <p className="text-sm text-white/70">Status</p>
              <Badge
                variant={project.status === "active" ? "default" : "secondary"}
              >
                {project.status}
              </Badge>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CalendarIcon className="mt-0.5 h-5 w-5 text-white/70" />
            <div>
              <p className="text-sm text-white/70">Timeline</p>
              <ClientOnly>
                <p>
                  {toShortDate(project.start_date)} -{" "}
                  {project.end_date ? toShortDate(project.end_date) : "Ongoing"}
                </p>
              </ClientOnly>
            </div>
          </div>
          {project.description && (
            <div className="flex items-start gap-3">
              <InfoIcon className="mt-0.5 h-5 w-5 text-white/70" />
              <div>
                <p className="text-sm text-white/70">Description</p>
                <p>{project.description}</p>
              </div>
            </div>
          )}
          {project.tags && project.tags.length > 0 && (
            <div className="flex items-start gap-3">
              <TagIcon className="mt-0.5 h-5 w-5 text-white/70" />
              <div>
                <p className="text-sm text-white/70">Tags</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {project.tags.map((tag: string) => (
                    <Badge key={tag} variant="outline">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Client & Business Entity */}
      <div>
        <h3 className="mb-4 flex items-center gap-2 border-white/10 border-b pb-2 font-medium text-lg">
          <Building2Icon className="h-5 w-5 text-white/70" /> Client & Business
          Entity
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <UserIcon className="mt-0.5 h-5 w-5 text-white/70" />
            <div>
              <p className="text-sm text-white/70">Client</p>
              <p className="font-medium">{project.client_name}</p>
              <p className="text-white/70 text-xs">{project.client_email}</p>
              {project.client_status && (
                <Badge
                  variant={
                    project.client_status === "active" ? "default" : "secondary"
                  }
                  className="mt-1"
                >
                  {project.client_status}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Building2Icon className="mt-0.5 h-5 w-5 text-white/70" />
            <div>
              <p className="text-sm text-white/70">Business Entity</p>
              <p className="font-medium">{project.business_entity_name}</p>
              {project.business_entity_status && (
                <Badge
                  variant={
                    project.business_entity_status === "active"
                      ? "default"
                      : "secondary"
                  }
                  className="mt-1"
                >
                  {project.business_entity_status}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Resources */}
      <div className="md:col-span-2">
        <div className="mb-4 flex items-center justify-between border-white/10 border-b pb-2">
          <h3 className="flex items-center gap-2 font-medium text-lg">
            <UsersIcon className="h-5 w-5 text-white/70" /> Assigned Resources
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAssignResources}
            disabled={project.status !== "active"}
          >
            <Plus className="mr-2 h-4 w-4" />
            Assign Resources
          </Button>
        </div>
        <div className="flex flex-wrap gap-4">
          <If
            condition={project.project_resources?.length > 0}
            fallback={
              <p className="text-sm text-white/70">
                No resources assigned to this project.
              </p>
            }
          >
            {project.project_resources?.map((projectResource) => (
              <div
                key={projectResource.id}
                className="flex min-w-[220px] max-w-xs flex-col gap-1 rounded-lg bg-white/5 p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-white/70" />
                    <span className="font-medium">
                      {projectResource.resource_first_name}{" "}
                      {projectResource.resource_last_name}
                    </span>
                    <Badge
                      variant={
                        projectResource.resource_status === "active"
                          ? "default"
                          : "secondary"
                      }
                      className="ml-2"
                    >
                      {projectResource.resource_status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleEditRates(projectResource)}
                      title="Edit Rates"
                    >
                      <EditIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:bg-destructive/10"
                      onClick={() => handleRemoveResource(projectResource)}
                      title="Remove from Project"
                      disabled={project.status !== "active"}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-white/70 text-xs">
                  {projectResource.resource_email}
                </p>
                {projectResource.resource_phone && (
                  <p className="text-white/70 text-xs">
                    {projectResource.resource_phone}
                  </p>
                )}
                {projectResource.resource_skills &&
                  projectResource.resource_skills.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {projectResource.resource_skills.map((skill: string) => (
                        <Badge
                          key={skill}
                          variant="outline"
                          className="text-xs"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  )}
                {projectResource.resource_project_rates &&
                  projectResource.resource_project_rates.length > 0 && (
                    <div className="mt-2 border-white/10 border-t pt-2">
                      <p className="mb-1 text-white/70 text-xs">Rates:</p>
                      <div className="space-y-1">
                        {projectResource.resource_project_rates.map((rate) => {
                          const invoiceItem = getInvoiceItem(
                            rate.invoice_item_id,
                          )
                          const projectClientRate = projectClientRates?.find(
                            (pClientRate) =>
                              pClientRate.id === rate.project_client_rate_id,
                          )
                          return (
                            <div
                              key={rate.id}
                              className="flex items-center justify-between text-sm"
                            >
                              <span className="flex flex-col text-white/70 text-xs">
                                <span className="font-semibold">
                                  {invoiceItem?.name || "Unknown"}
                                </span>
                                <span className="font-light">
                                  {toDisplayLanguage(
                                    projectClientRate?.locale ?? "",
                                  )}
                                </span>
                              </span>
                              <ClientOnly>
                                <span className="font-medium">
                                  {toDisplayCurrency(
                                    rate.rate,
                                    projectClientRate?.currency,
                                  )}
                                </span>
                              </ClientOnly>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </If>
        </div>
      </div>

      {/* Assign Resources Modal */}
      <AssignResourcesModal
        open={assignResourcesModalOpen}
        onOpenChange={setAssignResourcesModalOpen}
        projectId={params.projectId}
      />

      {/* Edit Resource Rates Modal */}
      <EditResourceRatesModal
        open={editRatesModalOpen}
        onOpenChange={setEditRatesModalOpen}
        projectResource={selectedProjectResource}
        projectId={params.projectId}
      />

      {/* Remove Resource Modal */}
      <RemoveResourceFromProjectModal
        open={removeResourceModalOpen}
        onOpenChange={setRemoveResourceModalOpen}
        onConfirm={handleConfirmRemove}
        isLoading={deleteProjectResourceMutation.isPending}
        projectResource={selectedProjectResource}
        projectName={project.name}
      />
    </div>
  )
}
